import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ConditionFeatureFlag,
  ConditionStatementColumn,
  ConditionStatementProfileData,
  ConditionTextOperator,
} from '@makyoapp/makyo-common'
import { ColumnAdapter } from '@makyoapp/makyo-common/dist/adapter'
import { ArrowShortRight16, User16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, Tooltip } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { getOperatorDefaultOption } from '@/shared/condition'

import { ColumnTypeIcon } from '../ColumnTypeIcon'
import { RenderColumn } from '../RenderColumn'
import { VariableColumnDd } from '../VariableSelectV2/VariableColumnDd'
import { VariableWrapper } from '../VariableSelectV2/VariableWrapper'
import { useConditionRepeaterContext } from './ConditionRepeaterContext'
import { useConditionSelectContext } from './ConditionSelectContext'

export function ConditionProfileDataDD() {
  const { id, setOpen, isRhs, index } = useConditionSelectContext()
  const [show, setShow] = useBoolean(false)

  const { dbId, tables } = useDbClientContext()

  const { onChange, value } = useConditionRepeaterContext()
  const key = isRhs ? 'rhs' : 'lhs'

  const stt = value.conditions[index]?.[key] as ConditionStatementColumn | undefined

  const [col, setCol] = useState<string | undefined>(stt?.col)

  const [tblObj, colOptions] = useMemo(() => {
    const tblObj = tables.find((tb) => tb.id === window.appUserTableID)
    let cols = tblObj?.columns.filter((c) => ![ColumnTypes.Connection].includes(c.type)) ?? []
    return [
      tblObj,
      [
        { id: '', name: 'Select Column' } as ColumnAdapter,
        { id: '_id', name: 'Row ID', type: ColumnTypes.RowID } as ColumnAdapter,
        ...cols,
      ],
    ]
  }, [tables])

  return (
    <VariableWrapper
      id={id ? `${id}-profile-data` : undefined}
      show={show}
      setShow={setShow}
      title={<Trans>Profile Data</Trans>}
      icon={<User16 />}
    >
      <div className='flex-c flex-col'>
        <VariableColumnDd id={id ? `${id}-column` : undefined} setCol={setCol} col={col} colOptions={colOptions} />
        <div className='w-full px-2'>
          <Button
            className='w-full'
            disabled={!dbId || !col}
            onClick={() => {
              const nextCond = [...value.conditions]
              const isSame = !isRhs ? (nextCond[index].lhs as ConditionStatementProfileData)?.col === col : true
              const newColObj = colOptions.find((c) => c.id === col)
              const nextOp = isSame
                ? nextCond[index].operator
                : newColObj && tblObj
                  ? getOperatorDefaultOption(newColObj, tblObj, tables)
                  : ConditionTextOperator.IsNotEmpty

              nextCond[index] = {
                ...nextCond[index],
                [key]: {
                  ...nextCond[index][key],
                  flag: ConditionFeatureFlag.ProfileData,
                  col: col ?? '',
                } as unknown as ConditionStatementProfileData,
                operator: nextOp,
              }
              onChange?.({
                isAny: value.isAny,
                conditions: nextCond,
              })
              setShow(false)
              setOpen(false)
            }}
          >
            <Trans>Select</Trans>
          </Button>
        </div>
      </div>
    </VariableWrapper>
  )
}

export function ConditionProfileDataStatement({ statement }: { statement: ConditionStatementProfileData }) {
  const { t } = useTranslation()

  const { tables: tblOptions } = useDbClientContext()
  const col = statement.col

  const table = tblOptions.find((tb) => tb.id === window.appUserTableID)
  const column =
    col === '_id' ? { id: '_id', name: 'Row ID', type: ColumnTypes.RowID } : table?.columns.find((c) => c.id === col)
  const invalidUserTable = window.appUserTableID !== table?.id || !column

  // const {
  //   x,
  //   y,
  //   strategy,
  //   context,
  //   isOpen: isTipOpen,
  //   placement,
  //   arrowRef,
  //   getFloatingProps,
  //   refs,
  //   getReferenceProps,
  // } = useTip({
  //   placement: 'top',
  // })

  // if (!table || !column) return null

  return (
    <Tooltip
      content={
        invalidUserTable ? (
          <div className='flex-c red'>
            <MaterialIcon size={18} icon='error' fill />
            <Trans>Invalid profile data due to user table change.</Trans>
          </div>
        ) : (
          <div className='flex-c'>
            <div className='flex-c whitespace-nowrap'>
              <User16 width={14} height={14} />
              <Trans>Profile Data</Trans>
            </div>

            {column && (
              <div className='flex-c'>
                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <ColumnTypeIcon type={column?.type} size={14} />
                  {column?.name}
                </div>
              </div>
            )}
          </div>
        )
      }
    >
      {invalidUserTable ? (
        <div className='flex items-center gap-1 min-w-0 red'>
          <MaterialIcon size={16} fill icon='error' className='red' />
          <User16 width={14} height={14} />
          <Trans>Profile Data</Trans>
        </div>
      ) : (
        <div className='flex items-center gap-1 min-w-0'>
          <div className='text-m-ss uppercase' title={t('Profile Data')}>
            <User16 width={14} height={14} />
          </div>
          {column && (
            <RenderColumn className='min-w-0' textClassName='txt' withStyle width={14} height={14} col={column} />
          )}
        </div>
      )}
    </Tooltip>
  )
}
